import { useApi } from '@src/app/context/Api';
import { configuration } from '@src/entities/Alert/includes/configuration';
import {
  AlertEntity,
  DefaultAlertEntity,
  DefaultListAlertsPayload,
  ListAlertsData,
  ListAlertsPayload,
} from '@src/entities/Alert/includes/types';
import { AlertsTable } from '@src/entities/Alert/ui/components';
import { Modal } from '@src/entities/Alert/ui/components/Modal';
import { User } from '@src/entities/User/ui/components/User';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';

/**
 * Page: Alerts
 *
 * Provides the user with a screen listing alerts.
 */
export function Alerts() {
  const [showAll, setShowAll] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [currentEntity, setCurrentEntity] = useState<AlertEntity>(DefaultAlertEntity);

  const { clearApiCache, useApiQuery } = useApi('Alert');

  const [ListAlerts, { data, error, isFetching, isLoading, refetch }] = useApiQuery('ListAlerts') as [
    (args: ListAlertsPayload) => void,
    {
      data?: ListAlertsData;
      error?: unknown;
      isFetching: boolean;
      isLoading: boolean;
      refetch: () => void;
    },
  ];

  useEffect(() => {
    clearApiCache([{ apiKey: 'Alert', queryKeys: ['ListAlerts'] }]);
  }, [showAll, clearApiCache]);

  const alertsTableProps = {
    currentEntity,
    error,
    isLoading: isLoading || isFetching,
    key: `${configuration.slug}-table`,
    refetch,
    requestFunction: ListAlerts,
    requestKey: 'ListAlerts',
    rows: data?.content || [],
    setCurrentEntity,
    setShowAll,
    setShowModal,
    showAll,
    totalPages: data?.totalPages || 1,
    totalRecords: data?.totalElements || 0,
  };

  return (
    <ErrorHandler location="src/pages/private/Notifications/Alerts/Alerts.tsx">
      <motion.div
        animate="open"
        initial={{ opacity: 0 }}
        variants={{
          closed: { opacity: 0 },
          open: { opacity: 1 },
        }}
      >
        <Grid>
          <Grid.Col
            span={{
              xs: '100%',
            }}
          >
            <Heading offset={0}>{configuration.pluralName}</Heading>
          </Grid.Col>

          <Grid.Col
            span={{
              xs: '100%',
            }}
          >
            {!showAll && (
              <AlertsTable
                {...alertsTableProps}
                requestArgs={{
                  ...DefaultListAlertsPayload,
                  ...{ alertStatuses: ['TRIGGERED', 'ASSIGNED'] },
                }}
              />
            )}

            {showAll && <AlertsTable {...alertsTableProps} requestArgs={DefaultListAlertsPayload} />}
            <User.Capability attributes={['edit']} key="user-capability" resource="Notifications:Alerts">
              <Modal
                action="edit"
                currentEntity={currentEntity}
                isOpen={showModal}
                refetch={refetch}
                setCurrentEntity={setCurrentEntity}
                setIsOpen={setShowModal}
              />
            </User.Capability>
          </Grid.Col>
        </Grid>
      </motion.div>
    </ErrorHandler>
  );
}
